import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useUser } from "@/utils";
import { OnboardingSurveyDialog } from "./OnboardingSurveyDialog";

type OpenSurvey = () => void;

const OnboardingSurveyContext = createContext<OpenSurvey | undefined>(
  undefined,
);

/**
 * Owns the one and only survey dialog.
 *
 * There are two ways in — the cold-load gate and the account menu — but only one instance,
 * mounted here. That also keeps the dialog structurally outside the account DropdownMenu:
 * nested, the menu and the dialog are two focus-managing layers both restoring focus as one
 * closes and the other opens, and focus ends up on <body>.
 *
 * Mounted in index.tsx with the other providers, above the router.
 */
export function OnboardingSurveyProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: user } = useUser();
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<"auto" | "menu">("auto");
  // Once per page load, and never again: this survives StrictMode's double-invoked effect,
  // stops the dialog re-opening after an Esc, and stops a `myself` refetch racing a submit
  // back into an open dialog.
  const autoOpened = useRef(false);

  useEffect(() => {
    // `useUser()` serves a placeholder with id 0 until /api/myself/ settles, and TanStack
    // Query reports `isSuccess` while doing so — so `id !== 0` is the guard, not `isSuccess`.
    if (user.id === 0) return;
    if (!user.onboardingSurveyDue) return;
    if (autoOpened.current) return;
    autoOpened.current = true;
    setSource("auto");
    setOpen(true);
  }, [user.id, user.onboardingSurveyDue]);

  const openFromMenu = useCallback(() => {
    setSource("menu");
    setOpen(true);
  }, []);

  return (
    <OnboardingSurveyContext.Provider value={openFromMenu}>
      {children}
      <OnboardingSurveyDialog
        open={open}
        onOpenChange={setOpen}
        source={source}
      />
    </OnboardingSurveyContext.Provider>
  );
}

export function useOpenOnboardingSurvey(): OpenSurvey {
  const context = useContext(OnboardingSurveyContext);
  if (!context) {
    throw new Error(
      "useOpenOnboardingSurvey must be used within an OnboardingSurveyProvider",
    );
  }
  return context;
}
